import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEye, FaEdit, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axiosInstance from '../../../config/axios';
import { useUserRole } from '../../../hooks/useUserRole';
import { personalakteStyles as ps, colors } from '../../employees/styles/personalakteStyles';

interface WorkArea {
  id: number;
  name: string;
  color?: string;
  location?: { id: number; name: string } | number | null;
  location_name?: string;
  employees_count?: number;
  manager?: number | null;
  is_active?: boolean;
}

interface EmployeeBasic {
  id: number;
  first_name: string;
  last_name: string;
  work_areas?: number[];
  onboarding_status?: string;
}

export default function WorkAreasTab(): React.JSX.Element {
  const navigate = useNavigate();
  const { isAdmin } = useUserRole();
  const [workAreas, setWorkAreas] = useState<WorkArea[]>([]);
  const [employeeMap, setEmployeeMap] = useState<Record<number, string>>({});
  const [activeCountByWA, setActiveCountByWA] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [isAdmin]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [waRes, empRes] = await Promise.allSettled([
        axiosInstance.get(isAdmin ? '/api/admin/work-areas/' : '/api/work-areas/'),
        axiosInstance.get('/api/employees/'),
      ]);

      if (waRes.status === 'fulfilled') {
        setWorkAreas(waRes.value.data || []);
      } else {
        toast.error('Failed to load work areas');
      }

      if (empRes.status === 'fulfilled') {
        const employees: EmployeeBasic[] = Array.isArray(empRes.value.data)
          ? empRes.value.data
          : empRes.value.data?.results || [];
        const map: Record<number, string> = {};
        const countMap: Record<number, number> = {};
        employees.forEach(e => {
          map[e.id] = `${e.first_name} ${e.last_name}`;
          if (e.onboarding_status !== 'DEACTIVATED' && Array.isArray(e.work_areas)) {
            e.work_areas.forEach(waId => { countMap[waId] = (countMap[waId] || 0) + 1; });
          }
        });
        setEmployeeMap(map);
        setActiveCountByWA(countMap);
      }
    } catch (err) {
      console.error('Failed to load work areas:', err);
      toast.error('Failed to load work areas');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    navigate(`/working-areas/new?returnTo=${encodeURIComponent('/organization?tab=work-areas')}`);
  };

  const handleEdit = (wa: WorkArea) => {
    navigate(`/working-areas/${wa.id}`);
  };

  const handleView = (wa: WorkArea) => {
    navigate(`/working-areas/${wa.id}`);
  };

  const handleToggleActive = async (wa: WorkArea, e: React.MouseEvent) => {
    e.stopPropagation();
    const isActive = wa.is_active !== false;
    const action = isActive ? 'deactivate' : 'activate';
    const confirmed = window.confirm(`${isActive ? 'Deactivate' : 'Activate'} work area "${wa.name}"?`);
    if (!confirmed) return;

    try {
      await axiosInstance.patch(`/api/admin/work-areas/${wa.id}/`, { is_active: !isActive });
      toast.success(`Work area ${isActive ? 'deactivated' : 'activated'}`);
      loadData();
    } catch (err: any) {
      console.error(`Failed to ${action} work area:`, err);
      const message = err?.response?.data?.detail || `${action} failed`;
      toast.error(message);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: colors.textMuted }}>Loading...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: colors.navy }}>
          Work areas ({workAreas.length})
        </h3>
        {isAdmin && (
          <button onClick={handleAdd} className="hover-btn-primary" style={ps.buttonPrimary} data-testid="add-work-area-button">
            <FaPlus size={11} />
            Add work area
          </button>
        )}
      </div>

      {workAreas.length === 0 ? (
        <div style={{ ...ps.innerContainer, padding: 60, textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: 15, color: colors.textMuted }}>No work areas yet</p>
          <p style={{ margin: '8px 0 0 0', fontSize: 13, color: colors.textLight }}>
            Click "Add work area" to get started.
          </p>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Location</th>
              <th style={thStyle}>Manager</th>
              <th style={{ ...thStyle, textAlign: 'center', width: 120 }}>Employees</th>
              {isAdmin && <th style={{ ...thStyle, textAlign: 'center', width: 140 }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {workAreas.map((wa) => (
              <tr
                key={wa.id}
                className="hover-list-item"
                style={{
                  borderBottom: `1px solid ${colors.borderLight}`,
                  cursor: isAdmin ? 'pointer' : 'default',
                  opacity: wa.is_active === false ? 0.5 : 1,
                }}
                onClick={() => isAdmin && handleView(wa)}
              >
                <td style={tdStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {wa.color && (
                      <div style={{ width: 10, height: 10, borderRadius: 3, background: wa.color, flexShrink: 0 }} />
                    )}
                    <span style={{ fontWeight: 600, color: colors.navy }}>{wa.name}</span>
                  </div>
                </td>
                <td style={tdStyle}>{wa.location_name || (typeof wa.location === 'object' ? wa.location?.name : null) || '—'}</td>
                <td style={tdStyle}>{wa.manager ? (employeeMap[wa.manager] || '—') : '—'}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{activeCountByWA[wa.id] ?? 0}</td>
                {isAdmin && (
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                      <button onClick={(e) => { e.stopPropagation(); handleView(wa); }} title="View" style={actionBtnStyle}>
                        <FaEye size={13} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleEdit(wa); }} title="Edit" style={actionBtnStyle}>
                        <FaEdit size={13} />
                      </button>
                      <button
                        onClick={(e) => handleToggleActive(wa, e)}
                        title={wa.is_active !== false ? 'Deactivate' : 'Activate'}
                        style={{ ...actionBtnStyle, color: wa.is_active !== false ? colors.danger : colors.success }}
                      >
                        {wa.is_active !== false ? <FaToggleOn size={15} /> : <FaToggleOff size={15} />}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: colors.textLight,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  borderBottom: `1px solid ${colors.border}`,
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: 13,
  color: colors.textBody,
};

const actionBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 6,
  cursor: 'pointer',
  color: colors.textMuted,
  borderRadius: 6,
  transition: 'background 0.15s',
};
